import React, { FC, useCallback, useState } from 'react';
import styles from './index.module.scss';
import KeywordsPreview from './KeywordsPreview';
import KeywordsInput from './KeywordsInput';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { IKeyword, KEYWORD_LIMIT } from 'src/models/matchingProfiles/types';
import { Form, Button } from 'src/common';
import { useCreateMPKeywords } from 'src/models/matchingProfiles/hooks';
import KeywordsLimitWarnModal from './KeywordsLimitWarnModal';
import classNames from 'classnames';
import { trackMPEditAddKeywords } from 'src/segment/events';

interface IProps {
  keywords?: readonly IKeyword[];
  disabled: boolean;
  withPreview?: boolean;
  extendedVersion?: boolean;
  mpId: string;
  mpName: string;
  onPressEnter: () => void;
}

export const KeywordsArea: FC<IProps> = props => {
  const { keywords, disabled, withPreview, extendedVersion, mpId, mpName, onPressEnter } = props;
  const { t } = useTranslation();

  const [createMPKeywords] = useCreateMPKeywords();

  const [form] = Form.useForm();
  const [restKeywords, setRestKeywords] = useState<string[]>([]);
  const [showWarning, setShowWarning] = useState(false);
  const onShowWarning = useCallback(() => setShowWarning(true), []);
  const onHideWarning = useCallback(() => {
    setShowWarning(false);
    setRestKeywords([]);
  }, []);

  const onCreateKeywords = useCallback(
    (userInput?: string): void => {
      if (!userInput) return;
      const currentAmountOfKeywords = (keywords || []).length;
      let newKeywords = userInput
        .trim()
        .split(',')
        .map(_ => _.trim())
        .filter(_ => !!_);

      if (currentAmountOfKeywords + newKeywords.length > KEYWORD_LIMIT) {
        const sliceIndex = KEYWORD_LIMIT - currentAmountOfKeywords;
        setRestKeywords(newKeywords.slice(sliceIndex));
        newKeywords = newKeywords.slice(0, sliceIndex);
        onShowWarning();
      }

      if (!!newKeywords.length && !disabled) {
        createMPKeywords({
          matchingProfileId: mpId,
          keywords: newKeywords
        });
      }

      form.setFieldsValue({
        keyword: ''
      });
    },
    [createMPKeywords, disabled, form, keywords, mpId, onShowWarning]
  );

  const onCreateKeywordsButtonHandle = useCallback(() => {
    const value = form.getFieldValue('keyword');
    onCreateKeywords(value);

    if (value) {
      trackMPEditAddKeywords({ id: mpId, name: mpName });
    }
  }, [form, mpId, mpName, onCreateKeywords]);

  return (
    <>
      <div className={classNames(styles.container, { [styles.isDisabled]: disabled })}>
        <FontAwesomeIcon icon={faSearch} className={classNames(styles.searchIcon, { [styles.isDisabled]: disabled })} />
        <ul className={styles.keywordsList}>
          {withPreview && !!keywords?.length && <KeywordsPreview mpId={mpId} keywords={keywords} disabled={disabled} />}
          <KeywordsInput
            disabled={disabled}
            label={t('Common.addKeywordsFromPreview')}
            form={form}
            onCreateKeywords={onCreateKeywords}
            onPressEnter={onPressEnter}
          />
        </ul>
      </div>
      {extendedVersion && !disabled && (
        <Button type={'primary'} onClick={onCreateKeywordsButtonHandle} disabled={disabled}>
          {t('Common.addKeywords')}
        </Button>
      )}
      {showWarning && !!restKeywords.length && (
        <KeywordsLimitWarnModal onClose={onHideWarning} keywords={restKeywords} />
      )}
    </>
  );
};

export default KeywordsArea;
